/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

class FAQComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setupEventListeners();

  }


  setupEventListeners() {
    this.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const isExpanded = this.getAttribute('aria-expanded') === 'true';
    this.setAttribute('aria-expanded', !isExpanded);
    this.classList.toggle('active');
  }
}

customElements.define('faq-item', FAQComponent);

class FeaturedProductItem extends HTMLElement {
  connectedCallback() {
    // Get data from script tag
    const dataScript = this.querySelector('.featured-product-data');
    if (!dataScript) {
      console.error('Featured product data script not found');
      return;
    }

    try {
      const data = JSON.parse(dataScript.textContent);
      this.variants = data.variants || [];
      this.options = data.options || [];
      this.currentColor = (data.defaultColor || "").trim();
      this.currentVariantId = data.defaultVariant || "";
    } catch (error) {
      console.error("Error parsing featured product data", error);
      this.variants = [];
      this.options = [];
      this.currentColor = "";
      this.currentVariantId = "";
    }

    // Cache DOM elements
    this.sizeSelector = this.querySelector(".featured-product__size-selector");
    this.hiddenVariantInput = this.querySelector(".featured-product__variant-id");
    this.imageElement =
      this.querySelector(".featured-product__media-item.is-selected") ||
      this.querySelector(".featured-product__primary-image");
    this.priceElement = this.querySelector(".featured-product__price");
    this.mediaContainer = this.querySelector(".featured-product__media");

    // Listen for changes on the size selector
    if (this.sizeSelector) {
      this.sizeSelector.addEventListener("change", this.onSizeChange.bind(this));
      this.currentSize = (this.sizeSelector.value || "").trim();
    } else {
      this.currentSize = "";
    }

    // Listen for Shopify's "variant:changed" event from the main product
    document.addEventListener("variant:changed", this.onMainProductVariantChanged.bind(this));

    // Initial update
    this.updateVariant();
  }

  onSizeChange(event) {
    this.currentSize = (event.target.value || "").trim();
    this.updateVariant();
  }

  /**
   * When the main product's variant changes, update the color on the featured product
   * while keeping the current size.
   */
  onMainProductVariantChanged(event) {
    if (event.detail && event.detail.variant) {
      // Assume that the main product's color is provided in its first option.
      this.currentColor = (event.detail.variant.options[0] || "").trim();
      this.updateVariant();
    }
  }

  updateVariant() {
    // Determine which option is "color" and which is "size" using the passed options.
    let colorIndex = this.options.findIndex(opt => opt.toLowerCase().includes("color"));
    let sizeIndex = this.options.findIndex(opt => opt.toLowerCase().includes("size"));

    // Fallback: if not found, assume defaults (first option as color, second as size).
    if (colorIndex === -1) colorIndex = 0;
    if (sizeIndex === -1) sizeIndex = 1;

    const currentColorLower = this.currentColor.toLowerCase();
    const currentSizeLower = this.currentSize.toLowerCase();

    // Find the variant that matches the current color (from main product) and size (from the select).
    const matchingVariant = this.variants.find(variant => {
      const variantColor = (variant.options[colorIndex] || "").trim().toLowerCase();
      const variantSize = (variant.options[sizeIndex] || "").trim().toLowerCase();
      return variantColor === currentColorLower && variantSize === currentSizeLower;
    });

    if (matchingVariant) {
      // Update the hidden variant input.
      if (this.hiddenVariantInput) {
        this.hiddenVariantInput.value = matchingVariant.id;
      }

      // Update the displayed price.
      if (this.priceElement && matchingVariant.price) {
        // Optionally, format the price using Shopify's formatting.
        let currencyFormat = window.themeVariables.settings.currencyCodeEnabled
          ? window.themeVariables.settings.moneyWithCurrencyFormat
          : window.themeVariables.settings.moneyFormat;
        this.priceElement.innerHTML = formatMoney(matchingVariant.price, currencyFormat);
      }

      // Update the media container.
      if (this.mediaContainer) {
        let selectedItem = null;

        // First, try to match by the variant's featured_media id.
        if (matchingVariant.featured_media && matchingVariant.featured_media.id) {
          selectedItem = this.mediaContainer.querySelector(
            `.featured-product__media-item[data-media-id="${String(matchingVariant.featured_media.id)}"]`
          );
        }

        // Fallback: match an image whose data-variant attribute equals the current color.
        if (!selectedItem) {
          selectedItem = Array.from(this.mediaContainer.querySelectorAll(".featured-product__media-item"))
            .find(item =>
              (item.getAttribute("data-variant") || "").trim().toLowerCase() === currentColorLower
            );
        }

        if (selectedItem) {
          // Remove the "is-selected" class from all images.
          this.mediaContainer.querySelectorAll(".featured-product__media-item").forEach(item => {
            item.classList.remove("is-selected");
          });
          // Mark the matching image as selected.
          selectedItem.classList.add("is-selected");
          this.imageElement = selectedItem;
        } else {
          console.warn("No image found matching the current variant.");
        }
      }

      // Dispatch a custom event to signal that the featured variant has changed.
      this.dispatchEvent(new CustomEvent("featured-variant:changed", {
        detail: { variant: matchingVariant },
        bubbles: true
      }));
    } else {
      console.warn("No matching variant found for color:", this.currentColor, "and size:", this.currentSize);
    }
  }
}

window.customElements.define("featured-product-item", FeaturedProductItem);

// ============================================================================================================
// PRODUCT CARD SWIPER INITIALIZATION
// ============================================================================================================
// Initialize Swiper sliders on product cards for mobile devices
// This allows users to swipe through multiple images per color variant

class ProductCardSwiperManager {
  constructor() {
    this.swipers = new Map();
    this.init();
  }

  init() {
    // Wait for Swiper library to load
    if (typeof Swiper === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    // Initialize all product card swipers
    this.initializeAllSwipers();

    // Listen for color swatch changes to rebuild swipers
    document.addEventListener('change', (event) => {
      if (event.target.matches('.product-item-meta__swatch-list .color-swatch__radio')) {
        this.handleColorSwatchChange(event.target);
      }
    });

    // Reinitialize swipers when products are dynamically loaded (e.g., infinite scroll, filtering)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1 && (node.matches('product-item') || node.querySelector('product-item'))) {
            setTimeout(() => this.initializeAllSwipers(), 100);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  initializeAllSwipers() {
    const swiperContainers = document.querySelectorAll('.product-item-swiper');

    swiperContainers.forEach((container) => {
      // Only initialize visible swipers (not display: none)
      if (container.style.display === 'none') {
        return;
      }

      const productId = container.dataset.productId;
      const variantColor = container.dataset.variantColor || 'default';
      const swiperId = `${productId}-${variantColor}`;

      // Skip if already initialized
      if (this.swipers.has(swiperId) && this.swipers.get(swiperId).destroyed === false) {
        return;
      }

      // Only initialize if there are slides
      const slides = container.querySelectorAll('.swiper-slide');
      if (slides.length === 0) {
        return;
      }

      try {
        const swiperInstance = new Swiper(container, {
          loop: false,
          slidesPerView: 1,
          spaceBetween: 0,
          scrollbar: {
            el: container.querySelector('.swiper-scrollbar'),
            draggable: true,
            hide: true,
          },
          speed: 300,
          touchRatio: 1,
          threshold: 5,
          resistance: true,
          resistanceRatio: 0.85,
          preventClicks: false,
          preventClicksPropagation: false,
          // Prevent swiper from interfering with link clicks
          touchStartPreventDefault: false,
          on: {
            touchEnd: function (swiper, event) {
              // If the user didn't swipe much, allow the link click
              if (Math.abs(swiper.touches.diff) < 5) {
                const link = event.target.closest('a');
                if (link) {
                  // Let the click event propagate naturally
                  return true;
                }
              }
            }
          }
        });

        this.swipers.set(swiperId, swiperInstance);
      } catch (error) {
        console.error('Error initializing Swiper:', error);
      }
    });
  }

  handleColorSwatchChange(radioInput) {
    // Find the parent product-item element
    const productItem = radioInput.closest('product-item');
    if (!productItem) return;

    const selectedColor = radioInput.value.toLowerCase();

    // Find the swiper container within this product item
    const swiperContainer = productItem.querySelector('.product-item__swiper-container');
    if (!swiperContainer) return;

    // Find all swipers for this product
    const allSwipers = swiperContainer.querySelectorAll('.product-item-swiper');

    // Hide all swipers and show only the one matching the selected color
    allSwipers.forEach(swiperElement => {
      const swiperColor = swiperElement.dataset.variantColor;
      const productId = swiperElement.dataset.productId;
      const swiperId = `${productId}-${swiperColor}`;

      if (swiperColor === selectedColor) {
        // Show this swiper first
        swiperElement.style.display = '';

        // console.log(`🎯 Color selected: ${swiperColor}, Product ID: ${productId}`);

        let swiperInstance = this.swipers.get(swiperId);

        // console.log(`   Swiper instance:`, swiperInstance);
        // console.log(`   Current element:`, swiperElement);

        // Check if swiper needs initialization - be explicit about the check
        let needsInit = false;
        if (!swiperInstance) {
          needsInit = true;
          // console.log(`   ℹ️ No swiper instance found, needs initialization`);
        } else if (swiperInstance.destroyed) {
          needsInit = true;
          // console.log(`   ℹ️ Swiper was destroyed, needs re-initialization`);
        } else if (swiperInstance.el !== swiperElement) {
          // The swiper instance is for a different element! Need to reinitialize
          needsInit = true;
          // console.log(`   ⚠️ Swiper instance element mismatch! Destroying old and creating new`);
          // Destroy the old instance
          swiperInstance.destroy(true, true);
          this.swipers.delete(swiperId);
        } else {
          // console.log(`   ℹ️ Swiper exists and is active for correct element`);
        }

        if (needsInit) {
          // Need to initialize this swiper
          const slides = swiperElement.querySelectorAll('.swiper-slide');

          // console.log(`   Found ${slides.length} slides for ${swiperColor}`);

          if (slides.length > 0) {
            try {
              // Capture 'this' context
              const self = this;

              // Small delay to ensure display is applied before initialization
              setTimeout(() => {
                // Double-check the element is visible
                if (swiperElement.style.display === 'none') {
                  // console.warn(`⚠️ Element still hidden, skipping init for ${swiperColor}`);
                  return;
                }

                const newSwiperInstance = new Swiper(swiperElement, {
                  loop: false,
                  slidesPerView: 1,
                  spaceBetween: 0,
                  scrollbar: {
                    el: swiperElement.querySelector('.swiper-scrollbar'),
                    draggable: true,
                    hide: true,
                  },
                  speed: 300,
                  touchRatio: 1,
                  threshold: 5,
                  resistance: true,
                  resistanceRatio: 0.85,
                  preventClicks: false,
                  preventClicksPropagation: false,
                  touchStartPreventDefault: false,
                  on: {
                    touchEnd: function (swiper, event) {
                      if (Math.abs(swiper.touches.diff) < 5) {
                        const link = event.target.closest('a');
                        if (link) {
                          return true;
                        }
                      }
                    }
                  }
                });

                self.swipers.set(swiperId, newSwiperInstance);
                console.log(`✅ Swiper initialized for color: ${swiperColor}, ID: ${swiperId}`);
              }, 50);
            } catch (error) {
              console.error('❌ Error initializing Swiper:', error);
            }
          }
        } else {
          // Swiper already initialized, just update it
          if (swiperInstance && !swiperInstance.destroyed) {
            setTimeout(() => {
              swiperInstance.slideTo(0, 0); // Go to first slide
              swiperInstance.update(); // Update swiper dimensions
              console.log(`🔄 Swiper updated for color: ${swiperColor}`);
            }, 50);
          } else {
            console.error(`❌ Tried to update swiper but instance is invalid:`, swiperInstance);
          }
        }
      } else {
        // Hide other swipers
        swiperElement.style.display = 'none';
      }
    });
  }

  destroySwiper(swiperId) {
    const swiperInstance = this.swipers.get(swiperId);
    if (swiperInstance && !swiperInstance.destroyed) {
      swiperInstance.destroy(true, true);
      this.swipers.delete(swiperId);
    }
  }

  destroyAllSwipers() {
    this.swipers.forEach((swiperInstance, swiperId) => {
      if (!swiperInstance.destroyed) {
        swiperInstance.destroy(true, true);
      }
    });
    this.swipers.clear();
  }
}

// Initialize the manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.productCardSwiperManager = new ProductCardSwiperManager();
  });
} else {
  window.productCardSwiperManager = new ProductCardSwiperManager();
}